import constants from '@playcastdotio/GlobalConfig';
import { Button, buttonVariants } from '@playcastdotio/Shadcn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPersonRunningFast, faGearComplexCode, faVideoArrowUpRight } from '@fortawesome/pro-light-svg-icons';
import { Link } from 'react-router-dom';
import { cn, isMobileDevice } from '../../utility/base';
import { download } from '../../utility/api';
import { faWindows } from '@fortawesome/free-brands-svg-icons';
import { useEffect } from 'react';
import ReactPlayer from 'react-player/youtube';
import { Helmet } from 'react-helmet-async';

const structuredData = {
  '@context': 'http://schema.org',
  '@type': 'Organization',
  name: 'Playcast',
  url: 'https://playcast.io',
  logo: 'https://playcast.io/images/playcast_logo-96x96.png',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+1-800-555-5555',
      contactType: 'Customer Service',
      areaServed: 'US',
      availableLanguage: 'English',
    },
  ],
  sameAs: [
    'https://discord.gg/7PYGxU68WH',
    'https://www.youtube.com/channel/UC-k16u9Ea_dvRhB70f-h5lQ',
    'https://www.twitch.tv/playcastio',
    'https://www.tiktok.com/@playcastdotio',
    'https://www.instagram.com/playcastio/',
  ],
};

export const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return (
    <>
      <Helmet>
        <title>Playcast - Interactive Game Streaming with Peer-to-Peer Connectivity</title>
        <meta
          name='description'
          content='Playcast offers interactive peer-to-peer game streaming, enabling real-time, low-latency gameplay. Join our community and experience seamless gaming connections.'
        />
        <script type='application/ld+json'>{JSON.stringify(structuredData)}</script>
      </Helmet>
      <div className='bg-content text-content-text m-0 h-auto w-full p-0'>
        <div className='bg-hero bg-content text-content-text m-0 h-auto w-full bg-cover bg-no-repeat p-0'>
          <div className='container mx-auto h-auto w-full px-8 pb-8 pt-20'>
            <section id='hero' className='grid grid-cols-1 gap-8 md:grid-cols-2'>
              <div className='order-last flex flex-col content-center gap-8 md:order-first'>
                <h1 className='row-auto text-3xl font-bold lg:text-4xl'>Cloudless Game Hosting</h1>
                <p className='row-auto text-lg'>
                  Play your games anywhere on any device. Share your game library with friends. Play together like never before.
                </p>
                <p className='row-auto text-lg'>All hosted from your home PC.</p>
                <div className='row-auto flex flex-col gap-2 md:flex-row'>
                  {!isMobileDevice() && (
                    <Button className='flex space-x-2 rounded-full' onClick={download}>
                      <FontAwesomeIcon icon={faWindows} className='text-2xl' />
                      <span>Download</span>
                    </Button>
                  )}
                  <Link to='/faqs' className={cn(buttonVariants({ variant: 'outline' }), 'hidden rounded-full md:flex')}>
                    Learn More
                  </Link>
                  <Link to='/faqs' className={cn(buttonVariants(), 'embossed-primary flex rounded-full md:hidden')}>
                    Learn More
                  </Link>
                </div>
                <p className='row-auto italic'>Release version {constants.VERSION}</p>
              </div>
              <div className='content-center'>
                <div className='group relative overflow-hidden rounded-lg'>
                  <ReactPlayer
                    url='https://www.youtube.com/watch?v=8pLmBnqusgQ'
                    controls
                    config={{
                      playerVars: {
                        modestbranding: 1, // Reduces YouTube branding
                        rel: 0, // Prevents showing related videos at the end
                        iv_load_policy: 3, // Hides video annotations
                      },
                    }}
                  />
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className='bg-content-secondary text-content-text m-0 h-auto w-full'>
          <div className='container mx-auto h-auto w-full px-0 py-20'>
            <section id='features' className='grid gap-4 md:grid-cols-3'>
              <div className='flex flex-col gap-4 text-center'>
                <FontAwesomeIcon icon={faPersonRunningFast} className='text-primary pt-3 text-8xl' />
                <h3 className='text-primary text-xl font-bold'>Simple to Setup</h3>
                <p className='px-6'>
                  From the download to install and some light setup, you'll be ready to play faster than your frozen burritos can dethaw in
                  the microwave.
                </p>
              </div>
              <div className='flex flex-col gap-4 text-center'>
                <FontAwesomeIcon icon={faGearComplexCode} className='text-primary pt-3 text-8xl' />
                <h3 className='text-primary text-xl font-bold'>OBS Integrated</h3>
                <p className='px-6'>
                  Already used to streaming with OBS? Don't worry, we've got you covered with native integrations to their app! Your scenes
                  are ready as is.
                </p>
              </div>
              <div className='flex flex-col gap-4 text-center'>
                <i className='fa-light fa-video-arrow-up-right text-primary pt-3 text-8xl'></i>
                <FontAwesomeIcon icon={faVideoArrowUpRight} className='text-primary pt-3 text-8xl' />
                <h3 className='text-primary text-xl font-bold'>Stream Anywhere</h3>
                <p className='px-6'>
                  Twitch, YouTube, Kick, or where ever you want to stream, we support it. You're in charge of your device, players, and how
                  you reach viewers.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

